import type { LinkingSuggestion } from "@/lib/api/types";

import { formatCount } from "./format";

export type StatusFilter = "offen" | "uebernommen" | "verworfen" | "alle";

export type Filters = {
  status: StatusFilter;
  orphan: boolean;
  deep: boolean;
  withSentence: boolean;
  minSimilarity: number;
};

export const DEFAULT_FILTERS: Filters = { status: "offen", orphan: false, deep: false, withSentence: false, minSimilarity: 0 };

export const STATUS_LABEL: Record<LinkingSuggestion["status"], string> = {
  offen: "Offen",
  uebernommen: "Übernommen",
  verworfen: "Verworfen",
};

/** Pfad statt voller Adresse (die Domain ist immer die eigene). */
export function shortUrl(url: string): string {
  try {
    const parsed = new URL(url);
    return `${parsed.pathname}${parsed.search}` || "/";
  } catch {
    return url;
  }
}

/** Score und Nähe im deutschen Format: 0,94. */
export function formatScore(value: number): string {
  return value.toLocaleString("de-DE", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

/** Satz in Teile vor, im und nach dem Anker, damit der Anker hervorgehoben werden kann. */
export function splitSentence(sentence: string, anchor: string): { pre: string; anchor: string; post: string } | null {
  const index = sentence.indexOf(anchor);
  if (index < 0 || anchor === "") return null;
  return { pre: sentence.slice(0, index), anchor, post: sentence.slice(index + anchor.length) };
}

export function isOrphan(item: LinkingSuggestion): boolean {
  return item.reasons.some((reason) => reason === "Orphan Page");
}

export function isDeep(item: LinkingSuggestion): boolean {
  return (item.target_depth ?? 0) > 4;
}

export function filterSuggestions(items: LinkingSuggestion[], filters: Filters): LinkingSuggestion[] {
  return items.filter(
    (item) =>
      (filters.status === "alle" || item.status === filters.status) &&
      (!filters.orphan || isOrphan(item)) &&
      (!filters.deep || isDeep(item)) &&
      (!filters.withSentence || item.sentence !== null) &&
      item.similarity >= filters.minSimilarity,
  );
}

export function targetLine(target: { inlinks: number | null; depth: number | null }): string {
  const parts = [];
  if (target.depth !== null) parts.push(`Tiefe ${formatCount(target.depth)}`);
  if (target.inlinks !== null) parts.push(`${formatCount(target.inlinks)} Inlinks`);
  return parts.join(" · ");
}

export const RULES = [
  "Höchstens 3 neue Links je Quellseite und je Zielseite und Durchlauf",
  "Keine Links aus Navigation, Footer oder Teasern (sie zählen nicht als Fließtext-Links)",
  "Keine Vorschläge, wo schon ein Link besteht, und keine Beinahe-Duplikate als Ziel",
  "Der Anker steht in einem bestehenden Satz der Quellseite, ohne bestehende Links zu verändern",
];
